import type { MemberRole } from "@nabble/sdk";
import { Button } from "@nabble/ui";
import { useState } from "react";
import { RoleChips } from "../../../components/RoleChips";
import { RolePicker } from "../../../components/RolePicker";
import type { PickableRole } from "../../../model/organizationRoles";

interface MembershipRowProps {
  organizationName: string;
  held: MemberRole[];
  roles: PickableRole[];
  busy: boolean;
  onSave: (roleIds: string[]) => Promise<void>;
  onRemove: () => void;
}

/** One organization of a person: the roles held there, which can be edited, and leaving it. */
export function MembershipRow({ organizationName, held, roles, busy, onSave, onRemove }: MembershipRowProps): React.JSX.Element {
  const [editing, setEditing] = useState<string[] | null>(null);

  async function save(roleIds: string[]): Promise<void> {
    await onSave(roleIds);
    setEditing(null);
  }

  return (
    <div className="nb-stack" style={{ gap: "var(--space-2)" }}>
      <div className="nb-row-between" style={{ gap: "var(--space-3)" }}>
        <div className="nb-stack" style={{ gap: "var(--space-1)", minWidth: 0 }}>
          <strong className="nb-truncate">{organizationName}</strong>
          <RoleChips roles={held} />
        </div>
        <div style={{ display: "flex", gap: "var(--space-2)", alignItems: "center" }}>
          <Button
            label="Edit roles"
            variant="secondary"
            size="sm"
            disabled={busy || editing !== null}
            onClick={() => setEditing(held.map((r) => r.id))}
          />
          <Button label="Remove" variant="secondary" size="sm" disabled={busy} onClick={onRemove} />
        </div>
      </div>
      {editing ? (
        <div className="nb-stack" style={{ gap: "var(--space-2)" }}>
          <RolePicker roles={roles} value={editing} onChange={setEditing} label={`Roles in ${organizationName}`} />
          <div style={{ display: "flex", gap: "var(--space-2)", justifyContent: "flex-end" }}>
            <Button label="Cancel" variant="secondary" size="sm" onClick={() => setEditing(null)} />
            <Button label="Save roles" size="sm" loading={busy} disabled={editing.length === 0} onClick={() => void save(editing)} />
          </div>
        </div>
      ) : null}
    </div>
  );
}
